import { hubAxios } from "@helpers/lib/axios";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import type { ProductType } from "@memorang/types";
import type { ExamDetails } from "@memorang/types";
import type { CurrentExamContext } from "../contexts/types";

const examToProductIdsMapping: Record<
	string,
	{
		studyPackProductId: string;
		studyPackProductNumericId: number;
		practiceTestProductId: string;
		practiceTestProductNumericId: number;
	}
> = {
	"81839ecf-5d22-488c-8d0d-3b45aa967df1": {
		studyPackProductId: "124b7081-6cdf-46bd-b22e-9656911376cc",
		studyPackProductNumericId: 253,
		practiceTestProductId: "03225547-0191-46dc-87c9-ddc5eb739230",
		practiceTestProductNumericId: 254,
	},
};
export const getProductTitle = (bundleType: ProductType) => {
	switch (bundleType) {
		case "STUDY_PACK":
			return "Study-Pack";
		case "PRACTICE_TEST":
			return "Practice Tests";
		case "PREP":
			return "Prep";
		default:
			return "";
	}
};

/** Premium access, or free collection is being served as the full unlocked catalog. */
export const hasUnlockedStudyPackContent = ({
	hasStudyPackContentAccess,
	freeStudyPackCollectionId,
}: {
	hasStudyPackContentAccess: boolean;
	freeStudyPackCollectionId?: number;
}): boolean => hasStudyPackContentAccess || Boolean(freeStudyPackCollectionId);

const constructClientExamContext = ({
	exam: {
		name,
		bundles,
		icon,
		studyPackCollection,
		freeStudypackCollectionId,
		id,
	},
	metadata: { examDate },
}: ExamDetails) => {
	const studyPack = bundles.find(
		(item) => item.bundle.bundleType === "STUDY_PACK",
	)?.bundle;
	const practiceTest = bundles.find(
		(item) => item.bundle.bundleType === "PRACTICE_TEST",
	)?.bundle;
	const prepBundle = bundles.find(
		(item) => item.bundle.bundleType === "PREP",
	)?.bundle;

	return {
		examName: name,
		examId: id,
		studyPackProductNumericId:
			studyPack?.numericId! ||
			examToProductIdsMapping[id]?.studyPackProductNumericId,
		studyPackProductId:
			studyPack?.id! || examToProductIdsMapping[id]?.studyPackProductId,
		practiceTestProductNumericId:
			practiceTest?.numericId ||
			examToProductIdsMapping[id]?.practiceTestProductNumericId,
		practiceTestProductId:
			practiceTest?.id! || examToProductIdsMapping[id]?.practiceTestProductId,
		prepNumericId: prepBundle?.numericId,
		prepId: prepBundle?.id,
		examDate: examDate,
		examIcon: icon,
		studyPackCollectionId: studyPackCollection[0],
		freeStudyPackCollectionId: freeStudypackCollectionId,
	};
};

const storeExamContextInKV = async (examContext: CurrentExamContext) => {
	try {
		await hubAxios.post(HUB_ENDPOINTS.EXAM_CONTEXT, { examContext });
	} catch (error) {
		const errorObj = new Error("Error storing exam context in KV");
		captureErrorToSentry(errorObj, {
			error: serializeErrorForSentry(error),
		});
	}
};

const retrieveExamContextFromKV = async () => {
	try {
		const response = await hubAxios.get(HUB_ENDPOINTS.EXAM_CONTEXT, {
			validateStatus: () => true,
		});
		if (response.status === 200) {
			return response.data as CurrentExamContext;
		}
		return null;
	} catch (error) {
		console.error("Error:", error);
	}
};

export {
	constructClientExamContext,
	storeExamContextInKV,
	retrieveExamContextFromKV,
};
